import {
  HeadContent,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router'
import appCss from '../styles.css?url'

// Initialize environment validation first

import type { QueryClient } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/sonner'
import { ThemeProvider } from 'next-themes'
import { authMiddleware } from '@/server/functions/auth'
import { getBaseUrl } from '@/server/functions/request'
import {
  createOGMetaTags,
  OGMetaTags,
} from '@/lib/og-config'

interface MyRouterContext {
  queryClient: QueryClient
}

const scripts: React.DetailedHTMLProps<
  React.ScriptHTMLAttributes<HTMLScriptElement>,
  HTMLScriptElement
>[] = []

if (import.meta.env.VITE_INSTRUMENTATION_SCRIPT_SRC) {
  scripts.push({
    src: import.meta.env.VITE_INSTRUMENTATION_SCRIPT_SRC,
    type: 'module',
  })
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  loader: async () => {
    const { currentUser } = await authMiddleware()
    const baseUrl = await getBaseUrl()

    return {
      currentUser,
      baseUrl,
    }
  },
  head: ({ loaderData }) => {
    const baseUrl =
      typeof window !== 'undefined'
        ? window.location.origin
        : (loaderData?.baseUrl ?? 'https://imagine.dev')

    const ogImageUrl = "https://quickclaw.org/default-og-image.png"

    const metadata: OGMetaTags = {
      title: 'QuickClaw — The Agentic Core',
      description:
        'Deploy autonomous agentic engines in seconds. High-bandwidth tool-use, multimodal payloads, and managed GCP infrastructure.',
      image: ogImageUrl,
      url: typeof window !== 'undefined' ? window.location.href : baseUrl,
    }

    const ogTags = createOGMetaTags(metadata)

    return {
      meta: [
        {
          charSet: 'utf-8',
        },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1',
        },
        {
          title: 'QuickClaw — The Agentic Core',
        },
        {
          name: 'description',
          content:
            'Deploy autonomous agentic engines in seconds. High-bandwidth tool-use, multimodal payloads, and managed GCP infrastructure.',
        },
        ...ogTags.meta,
      ],
      links: [
        {
          rel: 'icon',
          href: baseUrl + '/favicon.ico',
          type: 'image/x-icon',
        },
        {
          rel: 'stylesheet',
          href: appCss,
        },
        {
          rel: 'preconnect',
          href: 'https://fonts.googleapis.com',
        },
        {
          rel: 'preconnect',
          href: 'https://fonts.gstatic.com',
          crossOrigin: 'anonymous',
        },
        {
          rel: 'stylesheet',
          href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Outfit:wght@700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap',
        },
      ],
      scripts: [...scripts],
    }
  },

  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <script
          defer
          data-website-id="699197f0000d48035fb2"
          data-domain="quickclaw.org"
          src="https://www.insightly.live/script.js">
        </script>
        <script
          src="https://cdn.databuddy.cc/databuddy.js"
          data-client-id="b96b89bc-5642-42dc-8eec-8cadd123f11c"
          crossOrigin="anonymous"
          async>
        </script>
      </head>
      <body className="antialiased selection:bg-primary/30 selection:text-white">
        <ThemeProvider
          attribute="class"
          defaultTheme="dark"
          enableSystem
          disableTransitionOnChange
        >
          <div className="bg-halo" />
          <div className="bg-stars" />
          <div className="relative z-10">{children}</div>
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: 'rgba(10, 15, 26, 0.8)',
                border: '1px solid rgba(136, 146, 176, 0.2)',
                color: '#F0F4FF',
                backdropFilter: 'blur(10px)',
              },
            }}
          />
        </ThemeProvider>
        <Scripts />
      </body>
    </html>
  )
}
